import {
  concatBytes,
  getSubtleCrypto,
  lengthPrefixedUtf8,
  sha256,
  u8,
  u32be,
  utf8
} from "./encoding";
import { fail } from "./errors";

const DERIVE_MAGIC = utf8("YSI-DEAL-DERIVE-V1\0");
const U32_RANGE = 0x1_0000_0000;
const U32_MAX = 0xffff_ffff;

export const DOMAIN_TAG = {
  THEME: 0x01,
  LINEUP: 0x02,
  SEAT_PERMUTATION: 0x03
} as const;

export type DomainTag = (typeof DOMAIN_TAG)[keyof typeof DOMAIN_TAG];

export interface Uint32Source {
  nextUint32(): Promise<number>;
}

export async function deriveBlock(
  masterDigest: Uint8Array,
  tag: DomainTag,
  context: readonly string[],
  blockCounter: number,
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<Uint8Array> {
  if (masterDigest.length !== 32) {
    fail("INTEGER_OUT_OF_RANGE", "masterDigest must contain exactly 32 bytes");
  }
  return sha256(
    concatBytes(
      DERIVE_MAGIC,
      masterDigest,
      u8(tag),
      u32be(context.length),
      ...context.map(lengthPrefixedUtf8),
      u32be(blockCounter)
    ),
    subtle
  );
}

export class Sha256DomainStream implements Uint32Source {
  readonly #masterDigest: Uint8Array;
  readonly #tag: DomainTag;
  readonly #context: readonly string[];
  readonly #subtle: SubtleCrypto;
  #blockCounter = 0;
  #block: Uint8Array = new Uint8Array(0);
  #wordIndex = 8;

  constructor(
    masterDigest: Uint8Array,
    tag: DomainTag,
    context: readonly string[],
    subtle: SubtleCrypto = getSubtleCrypto()
  ) {
    if (masterDigest.length !== 32) {
      fail("INTEGER_OUT_OF_RANGE", "masterDigest must contain exactly 32 bytes");
    }
    this.#masterDigest = new Uint8Array(masterDigest);
    this.#tag = tag;
    this.#context = [...context];
    this.#subtle = subtle;
  }

  async nextUint32(): Promise<number> {
    if (this.#wordIndex >= 8) {
      if (this.#blockCounter > U32_MAX) {
        fail("RNG_EXHAUSTED", "SHA-256 domain stream counter exhausted");
      }
      this.#block = await deriveBlock(
        this.#masterDigest,
        this.#tag,
        this.#context,
        this.#blockCounter,
        this.#subtle
      );
      this.#blockCounter += 1;
      this.#wordIndex = 0;
    }

    const offset = this.#wordIndex * 4;
    const value =
      this.#block[offset]! * 0x100_0000 +
      this.#block[offset + 1]! * 0x1_0000 +
      this.#block[offset + 2]! * 0x100 +
      this.#block[offset + 3]!;
    this.#wordIndex += 1;
    return value;
  }
}

export async function uniformIndex(
  source: Uint32Source,
  size: number
): Promise<number> {
  if (!Number.isInteger(size) || size < 1 || size > U32_RANGE) {
    fail("INTEGER_OUT_OF_RANGE", `Invalid uniform range: ${String(size)}`);
  }
  if (size === 1) {
    return 0;
  }

  const limit = Math.floor(U32_RANGE / size) * size;
  for (;;) {
    const value = await source.nextUint32();
    if (!Number.isInteger(value) || value < 0 || value > U32_MAX) {
      fail("INTEGER_OUT_OF_RANGE", "Uint32Source returned an invalid word");
    }
    if (value < limit) {
      return value % size;
    }
  }
}

export interface FisherYatesResult<T> {
  readonly values: readonly T[];
  readonly swaps: readonly number[];
}

export async function fisherYates<T>(
  input: readonly T[],
  source: Uint32Source
): Promise<FisherYatesResult<T>> {
  const values = [...input];
  const swaps: number[] = [];
  for (let index = values.length - 1; index > 0; index -= 1) {
    const swapIndex = await uniformIndex(source, index + 1);
    swaps.push(swapIndex);
    [values[index], values[swapIndex]] = [values[swapIndex]!, values[index]!];
  }
  return { values, swaps };
}
